import React, { useMemo, useState } from "react";
import { T } from "../theme/tokens.js";
import { Modal, Btn, Notice, Chip } from "./ui/index.jsx";
import { supabase, friendlyError } from "../lib/supabase.js";

/* Saying, once, which school a register's name means.
 *
 * The two registers name the same school differently: one uses the layer's
 * code, the other a name somebody typed. The app can suggest which is which
 * and is right most of the time, but the ones it gets wrong are the ones
 * nobody would ever notice. A permit filed against the wrong school looks
 * exactly like a permit filed against the right one.
 *
 * So the suggestion is put in front of a person and nothing is saved until
 * they agree. What is stored is their decision.
 */

const input = {
  padding: "6px 8px", border: `1px solid ${T.line}`, borderRadius: 4,
  fontSize: 13, background: "#fff", color: T.ink, width: "100%",
};

export default function SiteMatcher({ contract, source, items, names, siteLabel, onClose, onSaved }) {
  const schools = useMemo(
    () => [...names].map(([code, name]) => ({ code, name })).sort((a, b) => a.name.localeCompare(b.name)),
    [names]);

  /* The suggestion is where each one starts, so the common case is a read
     through and one button. */
  /* A list per row, because one entry can genuinely be two schools. Most rows
     hold one and never grow a second. */
  const [picked, setPicked] = useState(
    () => Object.fromEntries(items.map((i) => [i.key, i.suggestion ? [i.suggestion.code] : [""]])));

  const setAt = (key, i, code) => setPicked((p) => {
    const next = [...(p[key] ?? [""])];
    next[i] = code;
    /* Choosing Not a school on a second row removes it rather than leaving a
       blank behind. */
    return { ...p, [key]: i > 0 && !code ? next.filter((_, n) => n !== i) : next };
  });
  const addAt = (key) => setPicked((p) => ({ ...p, [key]: [...(p[key] ?? [""]), ""] }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const chosen = Object.values(picked).filter((v) => v.some(Boolean)).length;

  const save = async () => {
    setBusy(true);
    setError("");
    const next = (contract.map_sources ?? []).map((s) => (s.id === source.id
      ? { ...s, aliases: { ...(s.aliases ?? {}),
          ...Object.fromEntries(Object.entries(picked)
            .map(([k, v]) => [k, v.filter(Boolean)])
            .filter(([, v]) => v.length)
            .map(([k, v]) => [k, v.length === 1 ? v[0] : v])) } }
      : s));
    const { error: failed } = await supabase
      .from("contracts").update({ map_sources: next }).eq("id", contract.id);
    setBusy(false);
    if (failed) { setError(friendlyError(failed)); return; }
    await onSaved?.();
    onClose();
  };

  const term = siteLabel.toLowerCase();

  return (
    <Modal title={`Which ${term} is each of these?`} onClose={onClose} wide>
      <div style={{ display: "grid", gap: 14 }}>
        <div style={{ fontSize: 13, color: T.soft, lineHeight: 1.65 }}>
          Your registers call the same {term} different things. The risks use the
          layer's code, the permits use a name somebody typed, and the board
          cannot tell they are the same, so it draws that {term} twice.
          {" "}Say which is which once and it is settled for good.
          <br /><br />
          Where the app is confident it has filled the answer in already. Read
          down and change anything that is wrong. Leave a row on
          {" "}<strong>Not a {term}</strong> if it genuinely is not one, and it stays off the
          board rather than being filed against the wrong place.
          <br /><br />
          Some entries are genuinely two {term}s. CAHS/LPK is one of those.
          Use <strong>This is more than one {term}</strong> and name both, and every row
          filed under it counts on both cards, because both of them are waiting
          on it.
        </div>

        <div style={{ display: "grid", gap: 8, maxHeight: 420, overflowY: "auto" }}>
          {items.map((item) => (
            <div key={item.key} style={{
              display: "grid", gridTemplateColumns: "minmax(140px, 1fr) 1.4fr auto",
              gap: 10, alignItems: "center", padding: "7px 2px",
              borderBottom: `1px solid ${T.line}`,
            }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: T.navy, overflowWrap: "break-word" }}>
                {item.value}
              </div>
              <div style={{ display: "grid", gap: 5 }}>
                {(picked[item.key] ?? [""]).map((code, i) => (
                  <select key={i} style={input} value={code}
                    onChange={(e) => setAt(item.key, i, e.target.value)}>
                    <option value="">{i === 0 ? `Not a ${term}` : `Remove this ${term}`}</option>
                    {schools.map((s) => (
                      <option key={s.code} value={s.code}>{s.name} ({s.code})</option>
                    ))}
                  </select>
                ))}
                {(picked[item.key] ?? [""]).every(Boolean) && (
                  <button type="button" onClick={() => addAt(item.key)}
                    style={{ background: "none", border: "none", color: T.steel,
                             fontSize: 12, cursor: "pointer", padding: 0, textAlign: "left" }}>
                    This is more than one {term}
                  </button>
                )}
              </div>
              <div style={{ minWidth: 78, textAlign: "right" }}>
                {item.suggestion
                  ? <Chip color={T.soft}>{Math.round(item.suggestion.score * 100)}% sure</Chip>
                  : <Chip color={T.soft}>No guess</Chip>}
              </div>
            </div>
          ))}
        </div>

        {error && <Notice kind="error">{error}</Notice>}

        <div style={{ display: "flex", gap: 8, justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontSize: 12.5, color: T.soft }}>
            {chosen} of {items.length} matched to a {term}.
            {chosen < items.length && ` The other ${items.length - chosen} stay off the board.`}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <Btn kind="ghost" onClick={onClose}>Cancel</Btn>
            <Btn onClick={save} disabled={busy}>{busy ? "Saving..." : "Save these answers"}</Btn>
          </div>
        </div>
      </div>
    </Modal>
  );
}
